import React from "react";
import OrdersAdmin from "../orders/admin/OrdersAdmin";
import Estimates from "../estimates/Estimates";
import RequestsAdmin from "../requests/admin/RequestsAdmin";
import RentalsAdmin from "../rentals/RentalsAdmin";
import Establishments from "../clients/Establishments";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { Alert, Card } from "react-bootstrap";
import Icon from "../sub/Icon";
import { connect } from "react-redux";
import MercurialStatus from "../../enums/MercurialStatus";
import RequestTypes from "../../enums/RequestTypes";
import "./SplitHome.css";

class SplitHomeAdmin extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      modal: null,
      windowWidth: window.innerWidth,
    };
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  handleResize = () => {
    this.setState({ windowWidth: window.innerWidth });
  };

  closeModal() {
    this.setState({ modal: null });
  }

  isWithinNext30Days(dateString) {
    const inputDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    return inputDate >= today && inputDate <= thirtyDaysFromNow;
  }

  getArrayLength(array) {
    return array && Array.isArray(array) ? array.length : 0;
  }

  getResponsiveIconSize() {
    const { windowWidth } = this.state;
    if (windowWidth < 480)
      return { width: "50px", height: "50px", fontSize: "1.2rem" };
    if (windowWidth < 640)
      return { width: "55px", height: "55px", fontSize: "1.3rem" };
    if (windowWidth < 768)
      return { width: "60px", height: "60px", fontSize: "1.4rem" };
    return { width: "65px", height: "65px", fontSize: "1.5rem" };
  }

  getStatsLayoutColumns() {
    const { windowWidth } = this.state;
    if (windowWidth < 480) return "1fr";
    if (windowWidth < 768) return "repeat(2, 1fr)";
    if (windowWidth < 1024) return "repeat(3, 1fr)";
    return "repeat(auto-fit, minmax(200px, 1fr))";
  }

  getCardPadding() {
    const { windowWidth } = this.state;
    if (windowWidth < 480) return "1.5rem";
    if (windowWidth < 768) return "1.8rem";
    return "2rem";
  }

  renderStatCard(stat, index) {
    const iconSize = this.getResponsiveIconSize();
    const { windowWidth } = this.state;

    // Layout adaptatif selon la taille d'écran
    const isCompactLayout = windowWidth < 640;
    const isStackedLayout = windowWidth < 480;

    return (
      <div
        key={index}
        className={`stat-card ${isCompactLayout ? "compact" : ""} ${isStackedLayout ? "stacked" : ""}`}
      >
        <div className="stat-icon-container">
          <div
            className="stat-icon"
            style={{
              ...iconSize,
              background: stat.color,
            }}
          >
            <Icon icon={stat.icon} style={{ fontSize: iconSize.fontSize }} />
          </div>
        </div>
        <div className="stat-content">
          <div className="stat-number">{stat.value}</div>
          <div className="stat-label">
            <FormattedMessage id={stat.label} />
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { mercurials, windowWidth } = this.props;
    const statsColumns = this.getStatsLayoutColumns();

    let hasMercurialToExtend = false;

    if (mercurials && mercurials.length !== 0) {
      for (const mercurial of mercurials) {
        const isOutDated = new Date(mercurial.end_date) < new Date();
        const isInactive = mercurial.status === MercurialStatus.INACTIVE;

        if (
          !isInactive &&
          !isOutDated &&
          this.isWithinNext30Days(mercurial.end_date)
        ) {
          hasMercurialToExtend = true;
          break;
        }
      }
    }

    // Compteurs pour les statistiques
    const ordersCount = this.getArrayLength(this.props.orders);
    // Les estimates sont des requests de type NEW_MATERIAL
    const estimatesCount = this.props.requests
      ? this.props.requests.filter(
          (request) => request.request_type === RequestTypes.NEW_MATERIAL,
        ).length
      : 0;
    const clientsCount = this.getArrayLength(this.props.establishments);
    const requestsCount = this.getArrayLength(this.props.requests);
    const rentalsCount =
      this.props.rentals && this.props.rentals.rentals
        ? this.props.rentals.rentals.length
        : 0;

    // Styles améliorés avec réactivité
    const modernHomepageStyle = {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #86c7ed 0%, #f18a4a 100%)",
      padding: windowWidth < 768 ? "1rem" : "2rem",
    };

    const statsStyle = {
      display: "grid",
      gridTemplateColumns: statsColumns,
      gap: windowWidth < 768 ? "1rem" : "1.5rem",
      marginBottom: windowWidth < 768 ? "1.5rem" : "2rem",
    };

    return (
      <div className="modern-homepage" style={modernHomepageStyle}>
        {/* Alerte pour les mercuriales à étendre */}
        {hasMercurialToExtend && (
          <div style={{ marginBottom: "2rem" }}>
            <Alert
              variant="warning"
              style={{
                borderRadius: "15px",
                border: "1px solid rgba(255, 193, 7, 0.3)",
                background: "rgba(255, 193, 7, 0.1)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Icon icon="clock" />{" "}
              <FormattedMessage id="Mercurial.Extend.Info" />
            </Alert>
          </div>
        )}

        {/* Stats Overview avec meilleure responsivité */}
        <div className="stats-overview" style={statsStyle}>
          {this.renderStatCard(
            {
              value: ordersCount,
              label: "Orders",
              icon: "shopping-cart",
              color: "linear-gradient(135deg, #2c5aa0 0%, #4a7bc8 100%)",
            },
            0,
          )}
          {this.renderStatCard(
            {
              value: rentalsCount,
              label: "Rentals",
              icon: "calendar",
              color: "linear-gradient(135deg, #28a745 0%, #5cb85c 100%)",
            },
            1,
          )}
          {this.renderStatCard(
            {
              value: requestsCount,
              label: "Interventions",
              icon: "wrench",
              color: "linear-gradient(135deg, #2c5aa0 0%, #4a7bc8 100%)",
            },
            2,
          )}
          {this.renderStatCard(
            {
              value: clientsCount,
              label: "Clients",
              icon: "users",
              color: "linear-gradient(135deg, #86c7ed 0%, #a8d5f2 100%)",
            },
            3,
          )}
          {this.renderStatCard(
            {
              value: estimatesCount,
              label: "Estimates",
              icon: "file-text",
              color: "linear-gradient(135deg, #f18a4a 0%, #ff9f6b 100%)",
            },
            4,
          )}
        </div>

        {/* Dashboard Grid avec CSS Grid Dense Flow - Layout Masonry */}
        <div className="dashboard-section">
          <div className="dashboard-card-item">
            <Card
              className="p-3"
              style={{
                borderRadius: "20px",
                boxShadow: "0 8px 25px rgba(0, 0, 0, 0.12)",
              }}
            >
              <Card.Title>
                <h4
                  style={{
                    color: "#1e3d72",
                    fontSize: windowWidth < 768 ? "1.1rem" : "1.25rem",
                  }}
                >
                  <FormattedMessage id="Summary.Orders.Admin" />
                </h4>
              </Card.Title>
              <div className="table-responsive">
                <OrdersAdmin limit={5} />
              </div>
              <Link to="/home/orders" className="overlay text-center"></Link>
            </Card>
          </div>

          <div className="dashboard-card-item">
            <Card
              className="p-3"
              style={{
                borderRadius: "20px",
                boxShadow: "0 8px 25px rgba(0, 0, 0, 0.12)",
              }}
            >
              <Card.Title>
                <h4
                  style={{
                    color: "#1e3d72",
                    fontSize: windowWidth < 768 ? "1.1rem" : "1.25rem",
                  }}
                >
                  <FormattedMessage id="Summary.Requests.Admin" />
                </h4>
              </Card.Title>
              <div className="table-responsive">
                <RequestsAdmin limit={5} />
              </div>
              <Link to="/home/requests" className="overlay text-center"></Link>
            </Card>
          </div>

          <div className="dashboard-card-item">
            <Card
              className="p-3"
              style={{
                borderRadius: "20px",
                boxShadow: "0 8px 25px rgba(0, 0, 0, 0.12)",
              }}
            >
              <Card.Title>
                <h4
                  style={{
                    color: "#28a745",
                    fontSize: windowWidth < 768 ? "1.1rem" : "1.25rem",
                  }}
                >
                  Synthèse des locations
                </h4>
              </Card.Title>
              <div className="table-responsive">
                <RentalsAdmin limit={5} />
              </div>
              <Link to="/home/rentals" className="overlay text-center"></Link>
            </Card>
          </div>

          <div className="dashboard-card-item">
            <Card
              className="p-3"
              style={{
                borderRadius: "20px",
                boxShadow: "0 8px 25px rgba(0, 0, 0, 0.12)",
              }}
            >
              <Card.Title>
                <h4
                  style={{
                    color: "#1e3d72",
                    fontSize: windowWidth < 768 ? "1.1rem" : "1.25rem",
                  }}
                >
                  <FormattedMessage id="Summary.Clients.Admin" />
                </h4>
              </Card.Title>
              <div className="table-responsive">
                <Establishments limit={5} />
              </div>
              <Link to="/home/clients" className="overlay text-center"></Link>
            </Card>
          </div>

          <div className="dashboard-card-item">
            <Card
              className="p-3"
              style={{
                borderRadius: "20px",
                boxShadow: "0 8px 25px rgba(0, 0, 0, 0.12)",
              }}
            >
              <Card.Title>
                <h4
                  style={{
                    color: "#b8460e",
                    fontSize: windowWidth < 768 ? "1.1rem" : "1.25rem",
                  }}
                >
                  <FormattedMessage id="Summary.Estimates.Admin" />
                </h4>
              </Card.Title>
              <div className="table-responsive">
                <Estimates limit={5} />
              </div>
              <Link to="/home/estimates" className="overlay text-center"></Link>
            </Card>
          </div>
        </div>
        {this.state.modal}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    mercurials: state.mercurials || [],
    orders: state.orders || [],
    establishments: state.establishments || [],
    requests: state.requests || [],
    rentals: state.rentals || {},
  };
};

export default connect(mapStateToProps)(SplitHomeAdmin);
